import { Core } from "@faire/design-tokens";
import { trackProductInfoPdpEcoFriendlyInfoShowDetailsClick } from "@faire/web-api/events/productInfo/click/pdpEcoFriendlyInfoShowDetails";
import { trackProductPageInformationSectionToggleClick } from "@faire/web-api/events/productPage/click/informationSectionToggle";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button/Button";
import { Chevron } from "@faire/web/slate/icons/Chevron";
import { Globe } from "@faire/web/slate/icons/foundation/Globe";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useState } from "@faire/web/ui/hooks/useState";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Suspense } from "@faire/web/ui/Suspense";
import React, { useMemo, useRef } from "react";
import styled, { css } from "styled-components";

import { FormattedText } from "@faire/retailer/components/FormattedText";
import { LazyEcoFriendlyInfoAccordionDetailsModalV2 } from "@faire/retailer/views/product/ProductDescriptionAccordions/LazyEcoFriendlyInfoAccordionDetailsModalV2";

import { StyledIconByName } from "../utils/StyledIconByName";

interface DescriptionSectionAccordionProps {
  section: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSectionGroup;
  productToken: string;
  productOptionToken?: string;
}

const MINIMUM_HEIGHT = 28;
const DEFAULT_LINE_HEIGHT = 20;

export const DescriptionSectionAccordion: React.FC<
  DescriptionSectionAccordionProps
> = ({ section, productOptionToken, productToken }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [hasExpandableContent, setHasExpandableContent] = useState(true);
  const accordionContentRef = useRef<HTMLDivElement>(null);
  const maxHeightOfTextWhenCollapsed =
    (section.max_preview_lines ?? 2) * DEFAULT_LINE_HEIGHT + MINIMUM_HEIGHT;

  React.useLayoutEffect(() => {
    if (!accordionContentRef.current) {
      return;
    }
    if (
      accordionContentRef.current.scrollHeight > maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(true);
    } else if (
      accordionContentRef.current.scrollHeight <= maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(false);
    }
  }, [maxHeightOfTextWhenCollapsed]);

  const toggleAccordion = () => {
    if (section.type === "PRODUCT_INFORMATION") {
      trackProductPageInformationSectionToggleClick(
        isAccordionOpen ? "close" : "open",
        productToken
      );
    }
    setIsAccordionOpen(!isAccordionOpen);
  };

  return (
    <>
      <Wrapper
        onClick={toggleAccordion}
        $isAccordionOpen={isAccordionOpen}
        $height={maxHeightOfTextWhenCollapsed}
      >
        <Column align="start" gap="2x" ref={accordionContentRef}>
          {section.sections.map((section) => {
            return (
              <InformationSection
                section={section}
                key={section.type}
                productOptionToken={productOptionToken}
                isAccordionOpen={isAccordionOpen}
              />
            );
          })}
        </Column>
        {hasExpandableContent ? (
          <>
            <Spacer width="3x" />
            <ChevronRow align="start">
              <AnimatedChevron $isAccordionOpen={isAccordionOpen}>
                <Chevron
                  data-test-id="accordion-chevron"
                  direction="right"
                  fontSize="16px"
                  color={Core.icon.default}
                />
              </AnimatedChevron>
            </ChevronRow>
          </>
        ) : null}
      </Wrapper>
      <Spacer size="3x" />
      <HorizontalLine />
    </>
  );
};

const InformationSection: React.FC<{
  section: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection;
  productOptionToken?: string;
  isAccordionOpen: boolean;
}> = ({ section, productOptionToken, isAccordionOpen }) => {
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const { strictLocalize } = useStrictLocalization();

  const originalEntries =
    section?.entries
      .map((entry) => entry.text?.original)
      .filter(isNotUndefined) ?? [];

  const toggleText = showOriginal
    ? strictLocalize({
        defaultMessage: "Translate",
        description: {
          text: "Button to let user see translated text",
        },
      })
    : strictLocalize({
        defaultMessage: "Show Original Language",
        description: {
          text: "Button to let user see original, untranslated text",
        },
      });

  const toggleLanguage = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowOriginal((value) => !value);
  };

  const translationText = showOriginal
    ? strictLocalize({
        defaultMessage: "This info is in its original language.",
      })
    : strictLocalize({
        defaultMessage: "This info has been automatically translated.",
      });

  const productOptionLevelEntries = useMemo(() => {
    if (!productOptionToken) {
      return [];
    }

    return section?.option_level_entries_by_option_token[productOptionToken]
      ?.entries;
  }, [section?.option_level_entries_by_option_token, productOptionToken]);

  const renderEntry = (
    entry: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IProductInformationSubSectionEntry
  ) => {
    const entryText = showOriginal
      ? entry.text?.original
      : entry.text?.translated ?? entry.text?.original;
    return (
      <Row>
        {entry.slate_icon ? (
          <IconWrapper>
            <StyledIconByName
              name={entry.slate_icon?.type}
              fontSize="16px"
              color={Core.icon.default}
              strokeWidth={1}
            />
          </IconWrapper>
        ) : null}
        <Typography maxLines={isAccordionOpen ? undefined : 2}>
          <FormattedText text={entryText} />
        </Typography>
      </Row>
    );
  };

  return (
    <Column align="start" key={section.type}>
      <Typography variant="paragraphSansMedium">
        {section.section_header_text}
      </Typography>
      <Spacer height={8} />
      <StyledSpan>
        {section?.entries.map(renderEntry)}
        {productOptionLevelEntries?.map(renderEntry)}
      </StyledSpan>
      {originalEntries.length === 0 ? null : (
        <>
          <Spacer height="2x" />
          <Row gap="1x">
            <Globe />
            <Typography>
              {translationText}{" "}
              <Button variant="tertiary" onClick={toggleLanguage}>
                {toggleText}
              </Button>
            </Typography>
          </Row>
        </>
      )}
      {section.modal_information_sections.length > 0 ? (
        <>
          <Button
            variant="tertiary"
            onClick={(event) => {
              setOpenDetailsModal(true);
              if (section.type === "ECO_FRIENDLY") {
                trackProductInfoPdpEcoFriendlyInfoShowDetailsClick();
              }
              // Stop propagate click event to accordion container
              event.stopPropagation();
              event.preventDefault();
            }}
          >
            <Typography color={Core.text.enabled}>
              <StrictLocalMsg
                defaultMessage="Show details"
                description={{
                  text: "Button to show details modal of the eco-friendly attributes of the product",
                  img: "https://cdn.faire.com/fastly/342841d7078860a99c7ef4d52def5e22a169f4c7d00d10cb7a90fc59c95be645.png",
                }}
              />
            </Typography>
          </Button>
          <Suspense fallback={null}>
            <LazyEcoFriendlyInfoAccordionDetailsModalV2
              ecoFriendlyInfoSection={section}
              onClose={() => setOpenDetailsModal(false)}
              isOpen={openDetailsModal}
            />
          </Suspense>
        </>
      ) : null}
      <Spacer height={8} />
    </Column>
  );
};

const Wrapper = styled(Row)<{ $isAccordionOpen?: boolean; $height?: number }>`
  width: 100%;
  ${(props) =>
    !props.$isAccordionOpen
      ? css`
          height: ${props.$height}px;
        `
      : ""}

  overflow: hidden;
  cursor: pointer;
`;

const ACCORDION_TRANSITION_TIMING = `
  transition-duration: 0.25s;
  transition-timing-function: ease-out;
`;
const AnimatedChevron = styled.div<{ $isAccordionOpen?: boolean }>`
  transform: rotate(
    ${({ $isAccordionOpen }) =>
      css`
        ${$isAccordionOpen ? -90 : 90}deg
      `}
  );
  transition-property: transform;
  ${ACCORDION_TRANSITION_TIMING}
`;

const ChevronRow = styled(Row)`
  margin-left: auto;
  margin-right: 0;
`;

const IconWrapper = styled(Column)`
  margin: 2px 12px 0 0;
`;

const StyledSpan = styled(Span)`
  text-align: start;
  word-break: break-word;
  white-space: pre-wrap;
`;
